<template>
  <div class="debt-installment-table">
    <b-table
      :items="tableData"
      :fields="tableFields"
      :busy="isLoading"
      class="debt-installment-table__table table-bordered table-hover"
      role="table"
      :aria-label="tableAriaLabel"
    >
      <!-- Custom table header -->
      <template slot="thead-top">
        <tr role="row">
          <th
            class="debt-installment-table__header text-center"
            role="columnheader"
          >STT</th>
          <th
            class="debt-installment-table__header text-center"
            role="columnheader"
          >Dịch vụ trả góp</th>
          <th
            class="debt-installment-table__header text-center"
            colspan="2"
            role="columnheader"
          >
            Phát sinh trong kì
          </th>
          <th
            class="debt-installment-table__header text-center"
            colspan="2"
            role="columnheader"
          >
            Số dư cuối kì
          </th>
        </tr>
      </template>

      <!-- Loading skeleton -->
      <template v-slot:table-busy>
        <vcl-table
          :speed="5"
          :animate="true"
          :columns="6"
        />
      </template>

      <!-- Index column -->
      <template v-slot:cell(index)="row">
        <span :aria-label="`Số thứ tự: ${row.index + 1 + offsetItems}`">
          {{ row.index + 1 + offsetItems }}
        </span>
      </template>

      <!-- Debt amount column -->
      <template v-slot:cell(debtAmount)="row">
        <span
          class="debt-installment-table__amount"
          :aria-label="`Số tiền nợ: ${formatCurrency(row.item.debtAmount)}`"
        >
          {{ formatCurrency(row.item.debtAmount) }}
        </span>
      </template>

      <!-- Paid amount column -->
      <template v-slot:cell(paidAmount)="row">
        <span
          class="debt-installment-table__amount"
          :aria-label="`Số tiền đã trả: ${formatCurrency(row.item.paidAmount)}`"
        >
          {{ formatCurrency(row.item.paidAmount) }}
        </span>
      </template>

      <!-- Debt sum column (when debt > paid) -->
      <template v-slot:cell(debtSum)="row">
        <span
          v-if="row.item.debtAmount > row.item.paidAmount"
          class="debt-installment-table__amount debt-installment-table__amount--debt"
          :aria-label="`Số tiền phải thu: ${formatCurrency(row.item.debtAmount - row.item.paidAmount)}`"
        >
          {{ formatCurrency(row.item.debtAmount - row.item.paidAmount) }}
        </span>
      </template>

      <!-- Paid sum column (when paid > debt) -->
      <template v-slot:cell(paidSum)="row">
        <span
          v-if="row.item.paidAmount > row.item.debtAmount"
          class="debt-installment-table__amount debt-installment-table__amount--credit"
          :aria-label="`Số tiền phải trả: ${formatCurrency(row.item.paidAmount - row.item.debtAmount)}`"
        >
          {{ formatCurrency(row.item.paidAmount - row.item.debtAmount) }}
        </span>
      </template>

      <!-- Summary footer row -->
      <template slot="bottom-row">
        <td
          colspan="2"
          class="debt-installment-table__summary-label text-right font-weight-bolder"
        >
          <span>Tổng</span>
        </td>
        <td class="debt-installment-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng tiền nợ: ${formatCurrency(totals.totalDebtAmount)}`">
            {{ formatCurrency(totals.totalDebtAmount) }}
          </span>
        </td>
        <td class="debt-installment-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng tiền đã trả: ${formatCurrency(totals.totalPaidAmount)}`">
            {{ formatCurrency(totals.totalPaidAmount) }}
          </span>
        </td>
        <td class="debt-installment-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng tiền phải thu: ${formatCurrency(totals.totalDebtSum)}`">
            {{ formatCurrency(totals.totalDebtSum) }}
          </span>
        </td>
        <td class="debt-installment-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng tiền phải trả: ${formatCurrency(totals.totalPaidSum)}`">
            {{ formatCurrency(totals.totalPaidSum) }}
          </span>
        </td>
      </template>
    </b-table>
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';
import { VclTable } from 'vue-content-loading';

export default {
  name: 'DebtInstallmentTable',
  components: {
    VclTable,
  },
  props: {
    /**
     * Table data array
     */
    data: {
      type: Array,
      default: () => [],
      validator: (data) => Array.isArray(data),
    },
    /**
     * Loading state
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Current page number for offset calculation
     */
    currentPage: {
      type: Number,
      default: 1,
      validator: (page) => page > 0,
    },
    /**
     * Items per page for offset calculation
     */
    pageSize: {
      type: Number,
      default: 10,
      validator: (size) => size > 0,
    },
    /**
     * Total amounts for summary calculations
     */
    totals: {
      type: Object,
      default: () => ({
        totalDebtAmount: 0,
        totalPaidAmount: 0,
        totalDebtSum: 0,
        totalPaidSum: 0,
      }),
    },
  },
  computed: {
    /**
     * Formatted table data
     */
    tableData() {
      return this.data || [];
    },

    /**
     * Calculate offset for row numbering
     */
    offsetItems() {
      return (this.currentPage - 1) * this.pageSize;
    },

    /**
     * Table accessibility label
     */
    tableAriaLabel() {
      return `Bảng công nợ dịch vụ trả góp với ${this.tableData.length} dòng dữ liệu`;
    },

    /**
     * Table field configuration
     */
    tableFields() {
      const commonStyle = {
        textAlign: 'center',
        fontWeight: 600,
        color: '#181c32',
        whiteSpace: 'break-spaces',
      };

      return [
        {
          key: 'index',
          label: '[1]',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '5%',
          },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: '[2]',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '35%',
          },
        },
        {
          key: 'debtAmount',
          label: 'Nợ\n[3]',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '15%',
          },
          tdClass: 'text-right break-line',
        },
        {
          key: 'paidAmount',
          label: 'Có\n[4]',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '15%',
          },
          tdClass: 'text-right break-line',
        },
        {
          key: 'debtSum',
          label: 'Nợ [Phải thu]\n= 3 - 4',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '15%',
          },
          tdClass: 'text-right break-line',
        },
        {
          key: 'paidSum',
          label: 'Có [Phải trả]\n= 4 - 3',
          sortable: false,
          thStyle: {
            ...commonStyle,
            width: '15%',
          },
          tdClass: 'text-right break-line',
        },
      ];
    },
  },
  methods: {
    /**
     * Format currency values
     * @param {number} value - Numeric value to format
     * @returns {string} Formatted currency string
     */
    formatCurrency(value) {
      return convertPrice(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-table {
  &__table {
    margin-bottom: 0;
  }

  &__header {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 0.75rem;
    vertical-align: middle;
  }

  &__amount {
    &--debt {
      color: #dc3545; // Red for debt
    }

    &--credit {
      color: #28a745; // Green for credit
    }
  }

  &__summary-label {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 0.75rem;
  }

  &__summary-value {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 0.75rem;
  }
}

// Responsive design
@media (max-width: 768px) {
  .debt-installment-table {
    &__table {
      font-size: 0.875rem;
    }

    &__header {
      padding: 0.5rem;
      font-size: 0.75rem;
    }

    &__summary-label,
    &__summary-value {
      padding: 0.5rem;
      font-size: 0.75rem;
    }
  }
}
</style>
